import { appendPaginationQuery } from '../../../types/pagination';
import type { ApiClient } from '../client';
import type { ApiResponse } from '../types';
import type {
  AllocateLeaveBalanceRequest,
  AllocateLeaveBalanceResponse,
  CreateStaffLeaveRequest,
  CreateStaffLeaveResponse,
  DecideStaffLeaveRequest,
  ListLeaveBalancesQuery,
  ListLeaveBalancesResponse,
  ListLeaveRequestsQuery,
  ListLeaveRequestsResponse,
  ListLeaveTypesResponse,
  UpsertLeaveTypeRequest,
  UpsertLeaveTypeResponse,
} from '../types/staff-leave';

export function bindStaffLeave(client: ApiClient) {
  return {
    async listStaffLeaveTypes(): Promise<ApiResponse<ListLeaveTypesResponse>> {
      return client.request<ListLeaveTypesResponse>('/staff-leave/types');
    },

    async createStaffLeaveType(
      body: UpsertLeaveTypeRequest
    ): Promise<ApiResponse<UpsertLeaveTypeResponse>> {
      return client.request<UpsertLeaveTypeResponse>('/staff-leave/types', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async updateStaffLeaveType(
      typeId: string,
      body: UpsertLeaveTypeRequest
    ): Promise<ApiResponse<UpsertLeaveTypeResponse>> {
      return client.request<UpsertLeaveTypeResponse>(`/staff-leave/types/${typeId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    async listStaffLeaveBalances(
      params?: ListLeaveBalancesQuery
    ): Promise<ApiResponse<ListLeaveBalancesResponse>> {
      const qs = new URLSearchParams();
      appendPaginationQuery(qs, params);
      if (params?.staff_id?.trim()) qs.set('staff_id', params.staff_id.trim());
      if (params?.year) qs.set('year', String(params.year));
      const query = qs.toString();
      return client.request<ListLeaveBalancesResponse>(
        query ? `/staff-leave/balances?${query}` : '/staff-leave/balances'
      );
    },

    async allocateStaffLeaveBalance(
      body: AllocateLeaveBalanceRequest
    ): Promise<ApiResponse<AllocateLeaveBalanceResponse>> {
      return client.request<AllocateLeaveBalanceResponse>('/staff-leave/balances', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async listStaffLeaveRequests(
      params?: ListLeaveRequestsQuery
    ): Promise<ApiResponse<ListLeaveRequestsResponse>> {
      const qs = new URLSearchParams();
      appendPaginationQuery(qs, params);
      if (params?.staff_id?.trim()) qs.set('staff_id', params.staff_id.trim());
      if (params?.status?.trim()) qs.set('status', params.status.trim());
      if (params?.year) qs.set('year', String(params.year));
      const query = qs.toString();
      return client.request<ListLeaveRequestsResponse>(
        query ? `/staff-leave/requests?${query}` : '/staff-leave/requests'
      );
    },

    async createStaffLeaveRequest(
      body: CreateStaffLeaveRequest
    ): Promise<ApiResponse<CreateStaffLeaveResponse>> {
      return client.request<CreateStaffLeaveResponse>('/staff-leave/requests', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async approveStaffLeaveRequest(
      requestId: string,
      body?: DecideStaffLeaveRequest
    ): Promise<ApiResponse<unknown>> {
      return client.request(`/staff-leave/requests/${requestId}/approve`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      });
    },

    async rejectStaffLeaveRequest(
      requestId: string,
      body: DecideStaffLeaveRequest
    ): Promise<ApiResponse<unknown>> {
      return client.request(`/staff-leave/requests/${requestId}/reject`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async cancelStaffLeaveRequest(requestId: string): Promise<ApiResponse<unknown>> {
      return client.request(`/staff-leave/requests/${requestId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },
  };
}
